import type { ProfileRoleBadgeItem } from '~/types/profile'
import {
  canAccessAdmin,
  isDeveloper,
  isLegacyAdmin,
  isManager,
  isSuperAdmin,
} from '~~/utils/auth/abilities'
import { computeProfileRights } from '~~/utils/auth/profileRightsMatrix'
import { useEffectiveGardianRoles } from '~/composables/auth/useEffectiveGardianRoles'
import { useOrganigrammeEditRouteAccess } from '~/composables/auth/useOrganigrammeEditRouteAccess'
import { useMyAgentAvatarSettings } from '~/composables/agent/useMyAgentAvatarSettings'
import { useMe } from '~/composables/auth/useMe'
import { buildGraphUserPrincipalName, formatPersonDisplayName } from '~~/utils/formatPersonName'

function formatFirstConnectionFrench(isoDate: string): string {
  const parsedDate = new Date(isoDate)
  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }
  const datePart = parsedDate.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const timePart = parsedDate.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })
  return `${datePart} à ${timePart}`
}

export function useAppLayoutProfile() {
  const runtimeConfig = useRuntimeConfig()
  const { user } = useOidcAuth()
  const { roles } = useEffectiveGardianRoles()
  const { me } = useMe()
  const { myOrgPositions } = useOrganigrammeEditRouteAccess()
  const {
    settings: myAvatarSettings,
    isReady: myAvatarSettingsReady,
    refresh: refreshMyAvatarSettings,
    update: updateMyAvatarSettings,
  } = useMyAgentAvatarSettings()

  const myNni = computed(() => me.value?.nni ?? null)

  const myAvatarDisplayMode = computed(
    () => (myAvatarSettings.value.avatarDisplayMode ?? 'teams') as 'teams' | 'initials' | 'avatar',
  )
  const myAvatarConfig = computed(
    () => (myAvatarSettings.value.avatarConfig ?? null) as Record<string, unknown> | null,
  )

  const profileRightsLabels = computed(() => computeProfileRights(roles.value, myOrgPositions.value))
  const userCanAccessAdmin = computed(() => canAccessAdmin(roles.value))

  const profileRoleBadges = computed<ProfileRoleBadgeItem[]>(() => {
    const roleList = roles.value
    const items: ProfileRoleBadgeItem[] = []
    if (isSuperAdmin(roleList)) {
      items.push({ label: 'Super administrateur', tagType: 'violet' })
    }
    if (isLegacyAdmin(roleList) && !isSuperAdmin(roleList)) {
      items.push({ label: 'Administrateur', tagType: 'primary' })
    }
    if (isDeveloper(roleList)) {
      items.push({ label: 'Développeur', tagType: 'light-blue' })
    }
    if (isManager(roleList)) {
      items.push({ label: 'Manageur applicatif', tagType: 'teal' })
    }
    if (items.length === 0) {
      items.push({ label: 'Consultation', tagType: 'neutral' })
    }
    return items
  })

  const userInfo = computed(
    () =>
      user.value?.userInfo ??
      ({} as {
        givenName?: string
        sn?: string
        email?: string
        departmentNumber?: string
      }),
  )

  const fullName = computed(() => {
    const sn = userInfo.value.sn
    const given = userInfo.value.givenName
    return formatPersonDisplayName(
      typeof sn === 'string' ? sn : '',
      typeof given === 'string' ? given : '',
    )
  })

  const userEmail = computed(() => {
    const email = userInfo.value.email
    return typeof email === 'string' ? email : ''
  })

  const myGraphUserPrincipalName = computed(() => {
    const userDomain = runtimeConfig.public.apigraphUserDomain
    return buildGraphUserPrincipalName(
      typeof userInfo.value.givenName === 'string' ? userInfo.value.givenName : null,
      typeof userInfo.value.sn === 'string' ? userInfo.value.sn : null,
      typeof userDomain === 'string' ? userDomain : null,
    )
  })

  const myGraphPhotoUrl = computed(() => {
    const tenantId = runtimeConfig.public.apigraphTenantId
    const clientId = runtimeConfig.public.apigraphClientId
    if (!tenantId || !clientId) {
      return null
    }
    if (!myGraphUserPrincipalName.value) {
      return null
    }
    return `/api/proxy/graph/photo?userPrincipalName=${encodeURIComponent(myGraphUserPrincipalName.value)}`
  })

  const departmentAttachment = computed(() => {
    const raw = userInfo.value.departmentNumber
    return typeof raw === 'string' ? raw.trim() : ''
  })

  const firstLoginDisplayFrench = computed(() => {
    const fromApi = me.value?.firstLoginAt
    if (fromApi) {
      const formatted = formatFirstConnectionFrench(fromApi)
      if (formatted.length > 0) {
        return formatted
      }
    }
    const timestamp = user.value?.loggedInAt
    if (!timestamp) {
      return ''
    }
    return formatFirstConnectionFrench(new Date(timestamp * 1000).toISOString())
  })

  return {
    myNni,
    myAvatarDisplayMode,
    myAvatarConfig,
    myAvatarSettings,
    myAvatarSettingsReady,
    refreshMyAvatarSettings,
    updateMyAvatarSettings,
    profileRightsLabels,
    userCanAccessAdmin,
    profileRoleBadges,
    fullName,
    userEmail,
    myGraphPhotoUrl,
    departmentAttachment,
    firstLoginDisplayFrench,
  }
}
